import { useState } from 'react'

import type {
  AttemptStatusFilter,
  PayloadWebhookAttempt,
} from '../../api/webhooks.ts'
import { navigateOnPlainClick } from '../../shell/links.ts'

import {
  AttemptsFrame,
  MetaRow,
  ResponseCell,
  WINDOW_OPTIONS,
  type AttemptColumn,
} from './AttemptsFrame.tsx'
import { formatAbsolute, formatRelative } from './format.ts'
import { PANEL_HAIRLINE } from './Panel.tsx'
import { PayloadPreview } from './PayloadPreview.tsx'
import { Pill } from './Pill.tsx'
import { usePayloadWebhookAttempts } from './useWebhookData.ts'

// What Curri sent for a payload, and what came back.
//
// A row is one *attempt*, not one event: a retried event appears once per
// attempt, which is why the detail names the event id — that is the key the
// partner's handler deduplicates on, and it is shared by every attempt of the
// same event.

const RETRIES_DOC_PATH = '/docs/webhooks/retries-and-failures'

const COLUMNS: AttemptColumn[] = [
  { header: 'Outcome', width: 'minmax(84px, 110px)' },
  { header: 'Sent', width: 'minmax(96px, 120px)' },
  { header: 'Event', width: 'minmax(150px, 1.2fr)' },
  { header: 'Payload', width: 'minmax(0, 190px)' },
  { header: 'Response', width: 'minmax(84px, 140px)' },
]

const BOX = {
  border: '1px solid var(--curri-border)',
  borderRadius: 12,
  padding: '14px 16px 6px',
} as const

const HEADING = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--curri-text-tertiary)',
} as const

const MONO = {
  fontFamily: 'var(--curri-font-mono)',
  fontSize: 12.5,
  color: 'var(--curri-text-secondary)',
} as const

const AttemptCells = ({ attempt }: { attempt: PayloadWebhookAttempt }) => (
  <>
    <span role="cell" style={{ justifySelf: 'start' }}>
      <Pill tone={attempt.success ? 'success' : 'danger'}>
        {attempt.success ? 'Delivered' : 'Failed'}
      </Pill>
    </span>
    <span role="cell" style={{ fontSize: 14 }}>
      <span style={{ whiteSpace: 'nowrap' }} title={formatAbsolute(attempt.sentAt)}>
        {formatRelative(attempt.sentAt)}
      </span>
    </span>
    <span role="cell" style={{ minWidth: 0 }}>
      <code style={MONO}>{attempt.eventType}</code>
      <span
        style={{
          marginLeft: 8,
          fontSize: 11.5,
          color: 'var(--curri-text-tertiary)',
          whiteSpace: 'nowrap',
        }}
      >
        attempt #{attempt.attemptNumber}
      </span>
    </span>
    <span
      role="cell"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}
    >
      <code
        style={{
          ...MONO,
          color: attempt.payloadExternalId
            ? 'var(--curri-text-secondary)'
            : 'var(--curri-text-tertiary)',
        }}
      >
        {attempt.payloadExternalId ?? 'Not available'}
      </code>
      {attempt.isSandbox && <Pill tone="neutral">Sandbox</Pill>}
    </span>
    <span
      role="cell"
      style={{
        textAlign: 'right',
        fontFamily: 'var(--curri-font-mono)',
        fontSize: 12.5,
      }}
    >
      <ResponseCell httpStatus={attempt.httpStatus} />
    </span>
  </>
)

const HeaderLine = ({ name, value }: { name: string; value: string }) => (
  <div
    style={{
      display: 'flex',
      gap: 10,
      padding: '7px 0',
      borderBottom: `1px solid ${PANEL_HAIRLINE}`,
      fontFamily: 'var(--curri-font-mono)',
      fontSize: 11,
    }}
  >
    <span style={{ flex: 'none', color: 'var(--curri-text-tertiary)' }}>
      {name}
    </span>
    <span
      style={{
        flex: 1,
        minWidth: 0,
        textAlign: 'right',
        color: 'var(--curri-text-secondary)',
        overflowWrap: 'anywhere',
      }}
    >
      {value}
    </span>
  </div>
)

const AttemptMeta = ({ attempt }: { attempt: PayloadWebhookAttempt }) => (
  <div style={BOX}>
    <MetaRow
      color={
        attempt.success ? 'var(--color-text-success)' : 'var(--color-text-danger)'
      }
      label="Status"
      value={attempt.httpStatus === null ? 'No response' : attempt.httpStatus}
    />
    <MetaRow label="Sent" value={formatAbsolute(attempt.sentAt)} />
    <MetaRow label="Event id" value={attempt.eventId} />
    <MetaRow label="Event time" value={formatAbsolute(attempt.eventCreatedAt)} />
    <MetaRow label="Attempt" value={`#${attempt.attemptNumber}`} />
    <MetaRow label="Duration" value={`${attempt.durationMs} ms`} />
    <MetaRow label="Mode" value={attempt.isSandbox ? 'Sandbox' : 'Production'} />
  </div>
)

const ResponseBody = ({ attempt }: { attempt: PayloadWebhookAttempt }) => (
  <div style={{ ...BOX, marginTop: 12, paddingBottom: 14 }}>
    <div style={HEADING}>Response</div>
    <p
      style={{
        margin: '9px 0 0',
        fontFamily: 'var(--curri-font-mono)',
        fontSize: 11.5,
        lineHeight: 1.7,
        color: attempt.responseBody
          ? 'var(--curri-text-secondary)'
          : 'var(--curri-text-tertiary)',
        overflowWrap: 'anywhere',
      }}
    >
      {attempt.responseBody ?? 'No response body'}
    </p>
  </div>
)

/**
 * Collapsed by default: the headers matter when a signature check fails, and
 * open they are eight mono lines between the reader and the response above.
 * The signature and any auth header arrive redacted from the API.
 */
const RequestHeaders = ({ headers }: { headers: Record<string, string> }) => (
  <details style={{ ...BOX, marginTop: 12, paddingBottom: 10 }}>
    <summary style={{ ...HEADING, cursor: 'pointer' }}>Request headers</summary>
    <div style={{ marginTop: 6 }}>
      {Object.entries(headers).map(([name, value]) => (
        <HeaderLine key={name} name={name} value={value} />
      ))}
    </div>
  </details>
)

const AttemptDetail = ({ attempt }: { attempt: PayloadWebhookAttempt }) => (
  <div
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 16,
      // The left inset (30px chevron column + 12px gap) tucks the detail under
      // the row's content rather than its affordance.
      padding: '9px 12px 18px 42px',
    }}
  >
    <div style={{ flex: '1.35 1 320px', minWidth: 0 }}>
      <PayloadPreview payload={attempt.body} />
    </div>
    <div style={{ flex: '1 1 240px', minWidth: 0 }}>
      <AttemptMeta attempt={attempt} />
      <ResponseBody attempt={attempt} />
      <RequestHeaders headers={attempt.requestHeaders} />
    </div>
  </div>
)

const EmptyBody = ({
  onNavigate,
  status,
}: {
  onNavigate?: (path: string) => void
  status: AttemptStatusFilter
}) => {
  if (status !== 'all') {
    return <>Try a wider window, or clear the filter to see every attempt.</>
  }

  return (
    <>
      Curri has not sent a payload webhook for your payloads in this window.
      Failed events are retried automatically, one ladder per event —{' '}
      <a
        href={RETRIES_DOC_PATH}
        onClick={onNavigate && navigateOnPlainClick(onNavigate, RETRIES_DOC_PATH)}
      >
        retries and failures
      </a>{' '}
      explains what your endpoint can expect.
    </>
  )
}

export const PayloadAttemptsCard = ({
  onNavigate,
  onWindowChange,
  windowDays,
}: {
  /** In-app routing for the docs link; without it the link is a full navigation. */
  onNavigate?: (path: string) => void
  onWindowChange: (next: number) => void
  windowDays: number
}) => {
  const [status, setStatus] = useState<AttemptStatusFilter>('all')
  const attempts = usePayloadWebhookAttempts({ windowDays, status })

  return (
    <AttemptsFrame
      columns={COLUMNS}
      description="Every payload webhook Curri sent for your payloads in the window, newest first, with the HTTP status your endpoint returned."
      emptyBody={<EmptyBody onNavigate={onNavigate} status={status} />}
      emptyTitle={
        status === 'all'
          ? `Nothing sent in the last ${windowDays} days`
          : `No ${status} attempts in the last ${windowDays} days`
      }
      isLoadingMore={attempts.isLoadingMore}
      keyOf={attempt => attempt.id}
      loadMore={attempts.loadMore}
      loadMoreFailed={attempts.loadMoreFailed}
      nextCursor={attempts.nextCursor}
      onStatusChange={setStatus}
      onWindowChange={onWindowChange}
      renderDetail={attempt => <AttemptDetail attempt={attempt} />}
      renderRow={attempt => <AttemptCells attempt={attempt} />}
      resource={attempts.resource}
      rowName={attempt => attempt.payloadExternalId}
      status={status}
      title="Payload webhook history"
      windowDays={windowDays}
      windowOptions={WINDOW_OPTIONS}
    />
  )
}
